import { useEffect, useMemo, useState } from "react";
import {
  createAdminTask,
  deleteAdminTask,
  getAdminTasks,
  getTaskPermissions,
  removeTaskPermission,
  updateAdminTask,
  updateTaskPermission,
} from "../api/instateamTasksAdminApi";
import { AppIcon } from "../components/InstateamMasterComponents";
import {
  addDays,
  buildTaskForm,
  formatTaskDate,
  formatTaskLabel,
  formatTaskMonth,
  getWeekDays,
  matchesTaskSearch,
  todayKey,
} from "../utils/taskHelpers";
import {
  getPresenceStatusMeta,
  resolveEmployeeProfileStatus,
} from "../utils/adminHelpers";

const EMPTY_TASK_FORM = buildTaskForm({}, todayKey());
const EMPTY_PERMISSION_FORM = {
  employeeId: "",
  canAssignTasks: true,
  allowAllEmployees: true,
  assignableDepartments: [],
  assignableEmployees: [],
};

export default function TasksPage({
  token,
  searchText = "",
  onSearchChange,
  onProtectedError,
  onNotify,
}) {
  const [selectedDate, setSelectedDate] = useState(todayKey());
  const [tasks, setTasks] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [employees, setEmployees] = useState([]);
  const [missedDates, setMissedDates] = useState([]);
  const [missedTasks, setMissedTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [assignedToFilter, setAssignedToFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState(EMPTY_TASK_FORM);
  const [isSavingTask, setIsSavingTask] = useState(false);
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
  const [permissionRows, setPermissionRows] = useState([]);
  const [permissionDepartments, setPermissionDepartments] = useState([]);
  const [permissionForm, setPermissionForm] = useState(EMPTY_PERMISSION_FORM);
  const [isSavingPermission, setIsSavingPermission] = useState(false);
  const [permissionActionKey, setPermissionActionKey] = useState("");

  async function fetchTasks() {
    if (!token) return;

    try {
      setIsLoading(true);
      const payload = await getAdminTasks(token, {
        date: selectedDate,
        assignedTo: assignedToFilter,
        priority: priorityFilter,
      });

      setTasks(payload.tasks || []);
      setMetrics(payload.metrics || {});
      setEmployees(payload.employees || []);
      setMissedDates(payload.missedDates || []);
      setMissedTasks(payload.missedTasks || []);
    } catch (error) {
      onNotify?.({ type: "error", text: error.message });
      onProtectedError?.(error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedDate, assignedToFilter, priorityFilter]);

  const visibleTasks = useMemo(
    () =>
      tasks.filter((task) => {
        const matchesStatus =
          statusFilter === "all" || task.effectiveStatus === statusFilter;
        return matchesStatus && matchesTaskSearch(task, searchText);
      }),
    [searchText, statusFilter, tasks]
  );

  const missedDateSet = new Set(missedDates);

  function openCreateTask() {
    setEditingTask(null);
    setTaskForm(buildTaskForm({}, selectedDate));
    setIsTaskModalOpen(true);
  }

  function openEditTask(task) {
    setEditingTask(task);
    setTaskForm(buildTaskForm(task, selectedDate));
    setIsTaskModalOpen(true);
  }

  function closeTaskModal() {
    if (isSavingTask) return;
    setIsTaskModalOpen(false);
    setEditingTask(null);
    setTaskForm(buildTaskForm({}, selectedDate));
  }

  async function submitTask(event) {
    event.preventDefault();

    if (!taskForm.title.trim() || !taskForm.details.trim() || !taskForm.assignedTo) {
      onNotify?.({
        type: "warning",
        text: "Task title, details, and assigned employee are required.",
      });
      return;
    }

    const payload = {
      ...taskForm,
      estimatedHours: Number(taskForm.estimatedHours || 0),
      estimatedExtraMinutes: Number(taskForm.estimatedExtraMinutes || 0),
    };

    try {
      setIsSavingTask(true);
      if (editingTask?._id) {
        await updateAdminTask(token, editingTask._id, payload);
        onNotify?.({ type: "success", text: "Task updated successfully." });
      } else {
        await createAdminTask(token, payload);
        onNotify?.({ type: "success", text: "Task created successfully." });
      }

      closeTaskModal();
      await fetchTasks();
    } catch (error) {
      onNotify?.({ type: "error", text: error.message });
      onProtectedError?.(error);
    } finally {
      setIsSavingTask(false);
    }
  }

  async function removeTask(task) {
    if (!window.confirm(`Delete "${task.title}"?`)) return;

    try {
      await deleteAdminTask(token, task._id);
      onNotify?.({ type: "success", text: "Task deleted successfully." });
      await fetchTasks();
    } catch (error) {
      onNotify?.({ type: "error", text: error.message });
      onProtectedError?.(error);
    }
  }

  async function openPermissionModal() {
    setIsPermissionModalOpen(true);
    try {
      await fetchTaskPermissions();
    } catch (error) {
      onNotify?.({ type: "error", text: error.message });
      onProtectedError?.(error);
    }
  }

  async function fetchTaskPermissions() {
    const payload = await getTaskPermissions(token);
    setPermissionRows(payload.employees || []);
    setPermissionDepartments(payload.departments || []);
    return payload;
  }

  function resetPermissionForm() {
    setPermissionForm(EMPTY_PERMISSION_FORM);
  }

  function selectPermissionEmployee(employeeId) {
    const row = permissionRows.find((item) => item._id === employeeId);
    setPermissionForm({
      employeeId,
      canAssignTasks: employeeId ? (row ? Boolean(row.canAssignTasks) : true) : false,
      allowAllEmployees: row?.allowAllEmployees !== false,
      assignableDepartments: row?.assignableDepartments || [],
      assignableEmployees: row?.assignableEmployees || [],
    });
  }

  function editPermission(permissionRow) {
    selectPermissionEmployee(permissionRow._id);
  }

  function toggleAssignableDepartment(department) {
    setPermissionForm((current) => {
      const currentDepartments = new Set(current.assignableDepartments || []);
      if (currentDepartments.has(department)) {
        currentDepartments.delete(department);
      } else {
        currentDepartments.add(department);
      }

      return {
        ...current,
        assignableDepartments: Array.from(currentDepartments),
      };
    });
  }

  function toggleAssignableEmployee(employeeId) {
    setPermissionForm((current) => {
      const currentIds = new Set(current.assignableEmployees || []);
      if (currentIds.has(employeeId)) {
        currentIds.delete(employeeId);
      } else {
        currentIds.add(employeeId);
      }

      return {
        ...current,
        assignableEmployees: Array.from(currentIds),
      };
    });
  }

  async function savePermission(event) {
    event.preventDefault();
    if (!permissionForm.employeeId) {
      onNotify?.({ type: "warning", text: "Select an employee first." });
      return;
    }

    try {
      setIsSavingPermission(true);
      await updateTaskPermission(token, permissionForm);
      await fetchTaskPermissions();
      selectPermissionEmployee(permissionForm.employeeId);
      onNotify?.({ type: "success", text: "Task assignment permission updated." });
    } catch (error) {
      onNotify?.({ type: "error", text: error.message });
      onProtectedError?.(error);
    } finally {
      setIsSavingPermission(false);
    }
  }

  async function deletePermission(permissionRow) {
    if (!permissionRow?._id) return;

    if (
      !window.confirm(
        `Remove task assignment rights for ${permissionRow.fullName}?`
      )
    ) {
      return;
    }

    try {
      setPermissionActionKey(`delete:${permissionRow._id}`);
      await removeTaskPermission(token, permissionRow._id);
      await fetchTaskPermissions();
      if (permissionForm.employeeId === permissionRow._id) {
        resetPermissionForm();
      }
      onNotify?.({
        type: "success",
        text: "Task assignment permission removed.",
      });
    } catch (error) {
      onNotify?.({ type: "error", text: error.message });
      onProtectedError?.(error);
    } finally {
      setPermissionActionKey("");
    }
  }

  function clearFilters() {
    setAssignedToFilter("all");
    setPriorityFilter("all");
    setStatusFilter("all");
    onSearchChange?.("");
  }

  return (
    <section className="task-page">
      <div className="task-page-toolbar">
        <div>
          <h1>Task Management</h1>
          <p>Dashboard &gt; Tasks</p>
        </div>
        <div className="task-toolbar-actions">
          <button className="secondary-button" type="button" onClick={openPermissionModal}>
            <AppIcon name="shieldLock" /> Manage Task Assignment Rights
          </button>
          <button className="primary-button" type="button" onClick={openCreateTask}>
            <AppIcon name="plus" /> Create Task
          </button>
        </div>
      </div>

      <TaskDateStrip
        selectedDate={selectedDate}
        onSelectDate={setSelectedDate}
        missedDateSet={missedDateSet}
      />

      <TaskMetrics metrics={metrics} />

      <div className="task-filter-row">
        <label>
          Assigned To
          <select value={assignedToFilter} onChange={(event) => setAssignedToFilter(event.target.value)}>
            <option value="all">All Employees</option>
            {employees.map((employee) => (
              <option key={employee._id} value={employee._id}>
                {employee.fullName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Priority
          <select value={priorityFilter} onChange={(event) => setPriorityFilter(event.target.value)}>
            <option value="all">All</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </label>
        <label>
          Status
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="all">All</option>
            <option value="todo">To Do</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="overdue">Overdue</option>
          </select>
        </label>
        <button className="secondary-button" type="button" onClick={clearFilters}>
          Clear Filters
        </button>
      </div>

      {missedTasks.length ? (
        <div className="task-alert-banner">
          <AppIcon name="infoCircle" />
          <span>
            {missedTasks.length} missed task{missedTasks.length > 1 ? "s" : ""} from{" "}
            {missedDates.slice(0, 3).map(formatTaskDate).join(", ")}.
          </span>
        </div>
      ) : null}

      <TaskTable
        tasks={visibleTasks}
        isLoading={isLoading}
        onEdit={openEditTask}
        onDelete={removeTask}
        showActions
      />

      {isTaskModalOpen ? (
        <TaskFormModal
          title={editingTask ? "Edit Task" : "Create Task"}
          taskForm={taskForm}
          setTaskForm={setTaskForm}
          employees={employees}
          onClose={closeTaskModal}
          onSubmit={submitTask}
          isSaving={isSavingTask}
        />
      ) : null}

      {isPermissionModalOpen ? (
        <PermissionModal
          employees={permissionRows}
          activePermissions={permissionRows.filter((employee) => employee.canAssignTasks)}
          departments={permissionDepartments}
          form={permissionForm}
          setForm={setPermissionForm}
          onSelectEmployee={selectPermissionEmployee}
          onToggleDepartment={toggleAssignableDepartment}
          onToggleEmployee={toggleAssignableEmployee}
          onEditPermission={editPermission}
          onDeletePermission={deletePermission}
          onResetForm={resetPermissionForm}
          onClose={() => setIsPermissionModalOpen(false)}
          onSubmit={savePermission}
          isSaving={isSavingPermission}
          actionKey={permissionActionKey}
        />
      ) : null}
    </section>
  );
}

function TaskDateStrip({ selectedDate, onSelectDate, missedDateSet }) {
  const weekDays = getWeekDays(selectedDate);

  return (
    <div className="task-date-strip">
      <div className="task-date-strip-head">
        <button type="button" onClick={() => onSelectDate(addDays(selectedDate, -7))}>
          <AppIcon name="chevronLeft" />
        </button>
        <strong>{formatTaskMonth(selectedDate)}</strong>
        <button type="button" onClick={() => onSelectDate(addDays(selectedDate, 7))}>
          <AppIcon name="chevronRight" />
        </button>
        <button type="button" onClick={() => onSelectDate(todayKey())}>
          Today
        </button>
        <input type="date" value={selectedDate} onChange={(event) => onSelectDate(event.target.value)} />
      </div>
      <div className="task-week-grid">
        {weekDays.map((day) => (
          <button
            key={day.key}
            type="button"
            className={`${day.key === selectedDate ? "active" : ""} ${missedDateSet.has(day.key) ? "missed" : ""}`}
            onClick={() => onSelectDate(day.key)}
          >
            <span>{day.weekday}</span>
            <strong>{day.day}</strong>
          </button>
        ))}
      </div>
    </div>
  );
}

function TaskMetrics({ metrics }) {
  const cards = [
    ["Total Tasks", metrics.totalTasks || 0, "calendar", "blue"],
    ["Estimated Time", metrics.estimatedTimeLabel || "0h 00m", "clock", "amber"],
    ["Completed", metrics.completed || 0, "check", "green"],
    ["In Progress", metrics.inProgress || 0, "attendance", "purple"],
    ["To Do", metrics.todo || 0, "tasks", "blue"],
    ["Overdue", metrics.overdue || 0, "infoCircle", "red"],
  ];

  return (
    <div className="task-metric-grid">
      {cards.map(([label, value, icon, tone]) => (
        <article className="task-metric-card" key={label}>
          <span className={`task-metric-icon ${tone}`}>
            <AppIcon name={icon} />
          </span>
          <div>
            <small>{label}</small>
            <strong>{value}</strong>
          </div>
        </article>
      ))}
    </div>
  );
}

function TaskTable({ tasks, isLoading, onEdit, onDelete, showActions = false }) {
  return (
    <div className="task-table-card">
      <div className="task-table-header">
        <span>Task Details</span>
        <span>Project</span>
        <span>Assigned By</span>
        <span>Assigned To</span>
        <span>Priority</span>
        <span>Est. Time</span>
        <span>Due Date</span>
        <span>Status</span>
        {showActions ? <span>Actions</span> : null}
      </div>

      {isLoading ? <div className="task-empty-row">Loading tasks...</div> : null}
      {!isLoading && !tasks.length ? <div className="task-empty-row">No tasks found.</div> : null}

      {!isLoading &&
        tasks.map((task) => (
          <div className="task-table-row" key={task._id}>
            <div className="task-title-cell">
              <strong>{task.title}</strong>
              <span>{task.details}</span>
            </div>
            <span>{task.project || "General"}</span>
            <PersonCell person={task.assignedBy} />
            <PersonCell person={task.assignedTo} />
            <span className={`task-pill priority-${task.priority}`}>
              {formatTaskLabel(task.priority)}
            </span>
            <span>{task.estimatedTimeLabel}</span>
            <span>
              {formatTaskDate(task.dueDate)}
              <small className={task.effectiveStatus === "overdue" ? "task-date-overdue" : ""}>
                {task.effectiveStatus === "overdue" ? "Overdue" : task.dueDate === todayKey() ? "Today" : ""}
              </small>
            </span>
            <span className={`task-pill status-${task.effectiveStatus}`}>
              {formatTaskLabel(task.effectiveStatus)}
            </span>
            {showActions ? (
              <div className="task-action-cell">
                <button type="button" onClick={() => onEdit(task)} aria-label="Edit task">
                  <AppIcon name="edit" />
                </button>
                <button type="button" onClick={() => onDelete(task)} aria-label="Delete task">
                  <AppIcon name="trash" />
                </button>
              </div>
            ) : null}
          </div>
        ))}
    </div>
  );
}

function PersonCell({ person }) {
  const presenceMeta = getPresenceStatusMeta(resolveEmployeeProfileStatus(person));

  return (
    <div className="task-person-cell">
      <div className="employee-presence-avatar-wrap compact">
        {person?.profilePhotoUrl ? (
          <img src={person.profilePhotoUrl} alt="" />
        ) : (
          <span>{String(person?.fullName || "?").slice(0, 1).toUpperCase()}</span>
        )}
        {person?.userType === "employee" ? (
          <span
            className={`employee-presence-badge compact ${presenceMeta.className}`}
            title={presenceMeta.label}
            aria-label={presenceMeta.label}
          >
            {presenceMeta.icon ? <AppIcon name={presenceMeta.icon} /> : <span className="employee-presence-dot" />}
          </span>
        ) : null}
      </div>
      <div>
        <strong>{person?.fullName || "—"}</strong>
        <small>{person?.designation || person?.department || "—"}</small>
      </div>
    </div>
  );
}

function TaskFormModal({
  title,
  taskForm,
  setTaskForm,
  employees,
  onClose,
  onSubmit,
  isSaving,
}) {
  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <section className="employee-modal task-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <div className="employee-modal-title">
          <h3>{title}</h3>
          <p>Assign title, details, due date, priority, and estimated time.</p>
        </div>

        <form className="task-form-grid" onSubmit={onSubmit}>
          <label className="form-field">
            <span>Task Title</span>
            <div className="field-shell">
              <input value={taskForm.title} onChange={(event) => setTaskForm((current) => ({ ...current, title: event.target.value }))} placeholder="Enter task title" />
            </div>
          </label>
          <label className="form-field">
            <span>Project</span>
            <div className="field-shell">
              <input value={taskForm.project} onChange={(event) => setTaskForm((current) => ({ ...current, project: event.target.value }))} placeholder="Project name" />
            </div>
          </label>
          <label className="form-field task-form-wide">
            <span>Proper Details</span>
            <textarea value={taskForm.details} onChange={(event) => setTaskForm((current) => ({ ...current, details: event.target.value }))} placeholder="Write task details" />
          </label>
          <label className="form-field">
            <span>Assign To</span>
            <div className="field-shell select-shell">
              <select value={taskForm.assignedTo} onChange={(event) => setTaskForm((current) => ({ ...current, assignedTo: event.target.value }))}>
                <option value="">Select employee</option>
                {employees.map((employee) => (
                  <option key={employee._id} value={employee._id}>{employee.fullName}</option>
                ))}
              </select>
              <AppIcon name="chevronDown" />
            </div>
          </label>
          <label className="form-field">
            <span>Due Date</span>
            <div className="field-shell">
              <input type="date" value={taskForm.dueDate} onChange={(event) => setTaskForm((current) => ({ ...current, dueDate: event.target.value }))} />
            </div>
          </label>
          <label className="form-field">
            <span>Priority</span>
            <div className="field-shell select-shell">
              <select value={taskForm.priority} onChange={(event) => setTaskForm((current) => ({ ...current, priority: event.target.value }))}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
              <AppIcon name="chevronDown" />
            </div>
          </label>
          <div className="task-estimate-grid">
            <label className="form-field">
              <span>Hours</span>
              <div className="field-shell">
                <input type="number" min="0" value={taskForm.estimatedHours} onChange={(event) => setTaskForm((current) => ({ ...current, estimatedHours: event.target.value }))} />
              </div>
            </label>
            <label className="form-field">
              <span>Minutes</span>
              <div className="field-shell">
                <input type="number" min="0" max="59" value={taskForm.estimatedExtraMinutes} onChange={(event) => setTaskForm((current) => ({ ...current, estimatedExtraMinutes: event.target.value }))} />
              </div>
            </label>
          </div>
          <div className="task-modal-actions">
            <button className="secondary-button" type="button" onClick={onClose}>Cancel</button>
            <button className="primary-button" type="submit">{isSaving ? "Saving..." : "Save Task"}</button>
          </div>
        </form>
      </section>
    </div>
  );
}

function PermissionModal({
  employees,
  activePermissions,
  departments,
  form,
  setForm,
  onSelectEmployee,
  onToggleDepartment,
  onToggleEmployee,
  onEditPermission,
  onDeletePermission,
  onResetForm,
  onClose,
  onSubmit,
  isSaving,
  actionKey,
}) {
  const selectedEmployee = employees.find((employee) => employee._id === form.employeeId);
  const scopedEmployees = employees.filter((employee) => employee._id !== form.employeeId);

  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <section className="employee-modal task-permission-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <div className="employee-modal-title">
          <h3>Task Permissions</h3>
          <p>Select who can assign tasks and which employees they can assign to.</p>
        </div>
        <div className="task-permission-layout">
          <div className="task-permission-existing">
            <div className="task-permission-existing-head">
              <div>
                <h4>Created Rights</h4>
                <p>Edit or remove already assigned task assignment rights.</p>
              </div>
              <button type="button" className="secondary-button" onClick={onResetForm}>
                <AppIcon name="plus" /> New Right
              </button>
            </div>

            <div className="task-permission-existing-list">
              {activePermissions.length ? (
                activePermissions.map((employee) => {
                  const isDeleting = actionKey === `delete:${employee._id}`;
                  return (
                    <article className="task-permission-existing-card" key={employee._id}>
                      <div>
                        <strong>{employee.fullName}</strong>
                        <span>
                          {employee.designation || "Employee"} -{" "}
                          {employee.department || "No department"}
                        </span>
                        <small>
                          {employee.allowAllEmployees
                            ? "Can assign to all employees"
                            : `${employee.assignableDepartments?.length || 0} departments, ${
                                employee.assignableEmployees?.length || 0
                              } employees`}
                        </small>
                      </div>
                      <div className="task-permission-existing-actions">
                        <button
                          type="button"
                          className="secondary-button"
                          onClick={() => onEditPermission(employee)}
                        >
                          <AppIcon name="edit" /> Edit
                        </button>
                        <button
                          type="button"
                          className="secondary-button danger"
                          onClick={() => onDeletePermission(employee)}
                          disabled={isDeleting}
                        >
                          <AppIcon name="trash" /> {isDeleting ? "Removing..." : "Delete"}
                        </button>
                      </div>
                    </article>
                  );
                })
              ) : (
                <div className="task-permission-empty">
                  No task assignment rights created yet.
                </div>
              )}
            </div>
          </div>

          <form className="task-permission-form" onSubmit={onSubmit}>
            <div className="task-permission-form-head">
              <div>
                <h4>{selectedEmployee?.canAssignTasks ? "Edit Right" : "Manage Right"}</h4>
                <p>Select an employee and configure where they can assign tasks.</p>
              </div>
            </div>

            <label className="form-field">
              <span>Employee</span>
              <div className="field-shell select-shell">
                <select value={form.employeeId} onChange={(event) => onSelectEmployee(event.target.value)}>
                  <option value="">Select employee</option>
                  {employees.map((employee) => (
                    <option key={employee._id} value={employee._id}>
                      {employee.fullName}
                    </option>
                  ))}
                </select>
                <AppIcon name="chevronDown" />
              </div>
            </label>
            <div className="task-permission-toggles">
              <label>
                <input type="checkbox" checked={form.canAssignTasks} onChange={(event) => setForm((current) => ({ ...current, canAssignTasks: event.target.checked }))} />
                Can assign tasks
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={form.allowAllEmployees}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      allowAllEmployees: event.target.checked,
                      assignableDepartments: event.target.checked ? [] : current.assignableDepartments,
                      assignableEmployees: event.target.checked ? [] : current.assignableEmployees,
                    }))
                  }
                />
                Can assign to all employees
              </label>
            </div>

            {selectedEmployee ? (
              <div className="task-permission-summary">
                <strong>{selectedEmployee.fullName}</strong>
                <span>{selectedEmployee.designation || "Employee"} - {selectedEmployee.department || "No department"}</span>
              </div>
            ) : null}

            {form.canAssignTasks && !form.allowAllEmployees ? (
              <>
                <div className="task-permission-scope">
                  <label className="form-field">
                    <span>Departments allowed</span>
                    <div className="field-shell select-shell">
                      <select value="" onChange={(event) => event.target.value && onToggleDepartment(event.target.value)}>
                        <option value="">Select department</option>
                        {departments
                          .filter((department) => !form.assignableDepartments.includes(department))
                          .map((department) => (
                            <option key={department} value={department}>{department}</option>
                          ))}
                      </select>
                      <AppIcon name="chevronDown" />
                    </div>
                  </label>
                  <div className="task-permission-chip-list">
                    {(form.assignableDepartments || []).map((department) => (
                      <button key={department} type="button" onClick={() => onToggleDepartment(department)}>
                        {department}
                        <AppIcon name="close" />
                      </button>
                    ))}
                    {!form.assignableDepartments?.length ? <span>No departments selected</span> : null}
                  </div>
                </div>

                <div className="task-permission-scope">
                  <label className="form-field">
                    <span>Selected employees allowed</span>
                    <div className="field-shell select-shell">
                      <select value="" onChange={(event) => event.target.value && onToggleEmployee(event.target.value)}>
                        <option value="">Select employee</option>
                        {scopedEmployees
                          .filter((employee) => !form.assignableEmployees.includes(employee._id))
                          .map((employee) => (
                            <option key={employee._id} value={employee._id}>
                              {employee.fullName} - {employee.department || "No department"}
                            </option>
                          ))}
                      </select>
                      <AppIcon name="chevronDown" />
                    </div>
                  </label>
                  <div className="task-permission-chip-list">
                    {(form.assignableEmployees || []).map((employeeId) => {
                      const employee = employees.find((item) => item._id === employeeId);
                      return (
                        <button key={employeeId} type="button" onClick={() => onToggleEmployee(employeeId)}>
                          {employee?.fullName || "Employee"}
                          <small>{employee?.department || ""}</small>
                          <AppIcon name="close" />
                        </button>
                      );
                    })}
                    {!form.assignableEmployees?.length ? <span>No employees selected</span> : null}
                  </div>
                </div>
              </>
            ) : null}
            <div className="task-modal-actions">
              <button className="secondary-button" type="button" onClick={onClose}>Cancel</button>
              <button className="primary-button" type="submit">{isSaving ? "Saving..." : "Save Permission"}</button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
